import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { Empty, PageHead, Skel } from '../components/ui'
import { number, thaiDateTime } from '../lib/format'

type TargetKind = 'syslog_udp' | 'syslog_tcp' | 'http'
type WireFormat = 'cef' | 'json'

interface SIEMTarget {
  id: string
  kind: TargetKind
  host: string
  port: number
  format: WireFormat
  enabled: boolean
  created_at: string
}

interface SIEMStatus {
  last_seq: number
  latest_seq: number
  lag: number
  targets: number
  enabled: number
}

interface SIEMTestResult {
  target_id: string
  kind: TargetKind
  format: WireFormat
  sent: boolean
  line: string
}

interface TargetInput {
  id?: string
  kind: TargetKind
  host: string
  port: number
  format: WireFormat
  enabled: boolean
}

const kindLabel: Record<TargetKind, string> = {
  syslog_udp: 'Syslog UDP',
  syslog_tcp: 'Syslog TCP',
  http: 'HTTP',
}

const kindHint: Record<TargetKind, string> = {
  syslog_udp: 'ตัวรับ syslog ภายใน เช่น 10.0.12.20',
  syslog_tcp: 'ตัวรับ syslog ที่รักษาการเชื่อมต่อ เช่น collector.example.com',
  http: 'ปลายทาง HTTP ที่รับ POST (ไม่ใช่ HTTPS) เช่น siem.internal',
}

const formatLabel: Record<WireFormat, string> = {
  cef: 'CEF',
  json: 'JSON Lines',
}

const defaultPort: Record<TargetKind, number> = {
  syslog_udp: 514,
  syslog_tcp: 6514,
  http: 8088,
}

export function SIEM() {
  const qc = useQueryClient()
  const targetsQuery = useQuery({
    queryKey: ['siem-targets'],
    queryFn: () => api<SIEMTarget[]>('/v1/siem/targets'),
    staleTime: 15_000,
  })
  const statusQuery = useQuery({
    queryKey: ['siem-status'],
    queryFn: () => api<SIEMStatus>('/v1/siem/status'),
    staleTime: 10_000,
  })

  const [kind, setKind] = useState<TargetKind>('syslog_udp')
  const [host, setHost] = useState('')
  const [port, setPort] = useState(String(defaultPort.syslog_udp))
  const [format, setFormat] = useState<WireFormat>('cef')
  const [tested, setTested] = useState<SIEMTestResult | null>(null)

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['siem-targets'] })
    void qc.invalidateQueries({ queryKey: ['siem-status'] })
  }

  const create = useMutation({
    mutationFn: (input: TargetInput) => apiWrite<SIEMTarget>('/v1/siem/targets', 'POST', input),
    onSuccess: () => {
      setHost('')
      refresh()
    },
  })

  const toggle = useMutation({
    mutationFn: (input: TargetInput) => apiWrite<SIEMTarget>('/v1/siem/targets', 'POST', input),
    onSuccess: refresh,
  })

  const remove = useMutation({
    mutationFn: (id: string) => apiWrite<{ status: string }>(`/v1/siem/targets/${id}`, 'DELETE'),
    onSuccess: (_res, id) => {
      if (tested?.target_id === id) setTested(null)
      refresh()
    },
  })

  const test = useMutation({
    mutationFn: (id: string) => apiWrite<SIEMTestResult>(`/v1/siem/targets/${id}/test`, 'POST'),
    onSuccess: (res) => {
      setTested(res)
      refresh()
    },
  })

  function changeKind(next: TargetKind) {
    setKind(next)
    setPort(String(defaultPort[next]))
  }

  const portNumber = Number(port)
  const portValid = Number.isInteger(portNumber) && portNumber >= 1 && portNumber <= 65535
  const canSubmit = host.trim().length > 0 && portValid && !create.isPending
  const rows = targetsQuery.data ?? []
  const status = statusQuery.data

  const inputOf = (t: SIEMTarget): TargetInput => ({
    id: t.id, kind: t.kind, host: t.host, port: t.port, format: t.format, enabled: !t.enabled,
  })

  return (
    <>
      <PageHead
        title="ส่งออก SIEM"
        sub="ส่งร่องรอยการตรวจสอบแบบต่อเนื่องไปยังตัวรับ SIEM ภายในองค์กร"
        actions={status ? (
          <span className={`chip${status.lag > 0 ? ' gold' : ''}`}>
            ตำแหน่ง cursor {number(status.last_seq)}
          </span>
        ) : undefined}
      />

      <div className="grid-main">
        <div className="card card-pad">
          <div className="card-label">เพิ่มปลายทาง</div>
          <p className="dim" style={{ fontSize: 12.5, marginTop: 10, lineHeight: 1.7 }}>
            ทุกปลายทางที่เปิดใช้งานจะได้รับเหตุการณ์เดียวกันตามลำดับเลขที่ของ audit event
            ระบบจะเลื่อน cursor ก็ต่อเมื่อส่งครบทุกปลายทางแล้ว
          </p>

          <div className="row" style={{ gap: 12, marginTop: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="field" style={{ maxWidth: 150 }}>
              <label htmlFor="siem-kind">ชนิด</label>
              <select id="siem-kind" className="select" value={kind}
                onChange={(e) => changeKind(e.target.value as TargetKind)}>
                {(Object.keys(kindLabel) as TargetKind[]).map((k) => (
                  <option key={k} value={k}>{kindLabel[k]}</option>
                ))}
              </select>
            </div>
            <div className="field" style={{ flex: 1, minWidth: 220 }}>
              <label htmlFor="siem-host">โฮสต์</label>
              <input id="siem-host" className="input" value={host} placeholder={kindHint[kind]}
                onChange={(e) => setHost(e.target.value)} />
            </div>
            <div className="field" style={{ maxWidth: 110 }}>
              <label htmlFor="siem-port">พอร์ต</label>
              <input id="siem-port" className="input" inputMode="numeric" value={port}
                onChange={(e) => setPort(e.target.value.replace(/[^0-9]/g, ''))} />
            </div>
            <div className="field" style={{ maxWidth: 150 }}>
              <label htmlFor="siem-format">รูปแบบ</label>
              <select id="siem-format" className="select" value={format}
                onChange={(e) => setFormat(e.target.value as WireFormat)}>
                {(Object.keys(formatLabel) as WireFormat[]).map((f) => (
                  <option key={f} value={f}>{formatLabel[f]}</option>
                ))}
              </select>
            </div>
          </div>

          {!portValid && port.length > 0 ? (
            <div className="form-error">พอร์ตต้องอยู่ระหว่าง 1 ถึง 65535</div>
          ) : null}
          {create.isError ? (
            <div className="form-error">{create.error instanceof Error ? create.error.message : 'เพิ่มปลายทางไม่สำเร็จ'}</div>
          ) : null}

          <button className="btn btn-primary" style={{ marginTop: 16 }} disabled={!canSubmit}
            onClick={() => create.mutate({
              kind, host: host.trim(), port: portNumber, format, enabled: true,
            })}>
            {create.isPending ? 'กำลังบันทึก…' : 'เพิ่มปลายทาง'}
          </button>
        </div>

        <div className="card card-pad">
          <div className="row">
            <div className="card-label">ตำแหน่ง cursor</div>
            <span className={`chip${status?.lag ? ' gold' : ''}`} style={{ marginLeft: 'auto' }}>
              {status?.lag ? `ตามหลัง ${number(status.lag)} เหตุการณ์` : 'ตามทันเหตุการณ์ล่าสุด'}
            </span>
          </div>
          {statusQuery.isLoading ? <Skel h={64} /> : status ? (
            <div className="grid-3" style={{ marginTop: 14 }}>
              <div>
                <div className="dim" style={{ fontSize: 11 }}>ส่งถึง event</div>
                <div className="stat-value" style={{ fontSize: 24 }}>{number(status.last_seq)}</div>
              </div>
              <div>
                <div className="dim" style={{ fontSize: 11 }}>เหตุการณ์ล่าสุด</div>
                <div className="stat-value" style={{ fontSize: 24 }}>{number(status.latest_seq)}</div>
              </div>
              <div>
                <div className="dim" style={{ fontSize: 11 }}>ปลายทางที่เปิด</div>
                <div className="stat-value" style={{ fontSize: 24 }}>{number(status.enabled)}</div>
              </div>
            </div>
          ) : (
            <div className="dim" style={{ fontSize: 12, marginTop: 12 }}>ยังอ่านสถานะไม่ได้</div>
          )}
          <p className="dim" style={{ fontSize: 11.5, marginTop: 14, lineHeight: 1.8 }}>
            cursor เป็นรายองค์กร ไม่ใช่รายปลายทาง หากปลายทางใดล้มเหลว ระบบจะไม่เลื่อนตำแหน่ง
            และส่งชุดเดิมซ้ำในรอบถัดไป (at-least-once) เพื่อไม่ให้เกิดช่องว่างในหลักฐาน
          </p>
        </div>
      </div>

      {tested ? (
        <div className="card card-pad" style={{ marginTop: 18 }}>
          <div className="row">
            <span className="card-label">บรรทัดทดสอบที่ส่งออก</span>
            <span className="chip gold" style={{ marginLeft: 'auto' }}>
              {kindLabel[tested.kind]} · {formatLabel[tested.format]} · ส่งแล้ว
            </span>
          </div>
          <pre className="mono" style={{ fontSize: 11.5, marginTop: 10, padding: '12px 14px', overflowX: 'auto',
            border: '1px solid var(--color-line-strong)', borderRadius: 6, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {tested.line}
          </pre>
          <div className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>
            UDP ไม่มีการตอบรับจากปลายทาง — “ส่งแล้ว” หมายถึงระบบเขียนดาตาแกรมออกจากเครื่องนี้เรียบร้อย
          </div>
        </div>
      ) : null}

      <div className="card-label" style={{ marginTop: 26, marginBottom: 10 }}>ปลายทางที่ตั้งไว้</div>

      {targetsQuery.isLoading ? <Skel h={160} /> : rows.length === 0 ? (
        <div className="card">
          <Empty title="ยังไม่มีปลายทาง SIEM"
            body="เพิ่มปลายทางแรกด้านบน เพื่อให้ตัวรับ SIEM ได้รับเหตุการณ์ตรวจสอบแบบต่อเนื่อง" />
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>ชนิด</th><th>ปลายทาง</th><th>รูปแบบ</th><th>สถานะ</th><th>เพิ่มเมื่อ</th><th />
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => {
                const busyTest = test.isPending && test.variables === t.id
                const busyToggle = toggle.isPending && toggle.variables?.id === t.id
                const busyRemove = remove.isPending && remove.variables === t.id
                return (
                  <tr key={t.id} style={{ cursor: 'default' }}>
                    <td><span className="chip">{kindLabel[t.kind]}</span></td>
                    <td className="mono" style={{ fontSize: 11.5 }}>{t.host}:{t.port}</td>
                    <td className="dim">{formatLabel[t.format]}</td>
                    <td>
                      <button className={`chip${t.enabled ? ' gold' : ''}`} disabled={busyToggle}
                        style={{ cursor: 'pointer' }} onClick={() => toggle.mutate(inputOf(t))}>
                        {busyToggle ? 'กำลังบันทึก…' : t.enabled ? 'เปิดใช้งาน' : 'ปิดอยู่'}
                      </button>
                    </td>
                    <td className="dim" style={{ fontSize: 11.5 }}>{thaiDateTime(t.created_at)}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="btn btn-sm btn-ghost" disabled={busyTest}
                        onClick={() => test.mutate(t.id)}>
                        {busyTest ? 'กำลังส่ง…' : 'ทดสอบ'}
                      </button>
                      <button className="btn btn-sm btn-ghost" disabled={busyRemove}
                        style={{ marginLeft: 6 }} onClick={() => remove.mutate(t.id)}>
                        ลบ
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {test.isError ? (
        <div className="form-error" style={{ marginTop: 12 }}>
          {test.error instanceof Error ? test.error.message : 'ส่งข้อมูลทดสอบไม่สำเร็จ'}
        </div>
      ) : null}
      {toggle.isError || remove.isError ? (
        <div className="form-error" style={{ marginTop: 12 }}>
          {(toggle.error ?? remove.error) instanceof Error
            ? (toggle.error ?? remove.error)!.message
            : 'แก้ไขปลายทางไม่สำเร็จ'}
        </div>
      ) : null}

      <div className="dim" style={{ fontSize: 11.5, marginTop: 14 }}>
        CEF signature id คือชื่อเหตุการณ์ (เช่น finding.created) และระดับความรุนแรงแปลงเป็นสเกล 0–10
        ตามชนิดเหตุการณ์. ปลายทาง HTTP ใช้ POST ไปที่ http://โฮสต์:พอร์ต/ โดยไม่มี TLS ในรุ่นนี้
      </div>
    </>
  )
}
